import { FormControl, Select, Typography, MenuItem } from "@mui/material";
import { styled } from "@mui/material/styles";
import { myVJBlue200, myVJBlack200, myVJBlack100, myVJBlack400, myVJBlack900 } from "../../constants/brandingColors";
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExclamationError from "../Error/ExclamationError";

const FormControlBox = styled(FormControl)({
  display: "flex",
  flexDirection: "column",
});

const StyledInputBoxContainer = styled('div')(({ theme }) => ({
  marginBottom: theme.spacing(2)
}));

const TextContainer = styled("div")({
  alignSelf: "flex-start",
});

const ErrorContainer = styled("div")(({ theme }) => ({
  paddingTop: theme.spacing(.5),
}));

type optionType = {
  value: string;
  text: string;
};

interface SelectInputProps {
  fieldName: string;
  label: string;
  placeholder?: string;
  autoFocus?: boolean;
  onChange?: (event) => void;
  onClear?: (event) => void;
  isDisabled?: boolean;
  isRequired?: boolean;
  value?: string | number;
  error: any;
  isPercent?: boolean;
  id?: string;
  options: optionType[]
  dataTestId?: string;
}

function SelectInput({
  fieldName,
  label,
  onChange,
  value,
  isDisabled = false,
  isRequired = false,
  placeholder,
  autoFocus = false,
  error,
  options = [],
  id,
  dataTestId,
}: SelectInputProps) {

  const showPlaceholder = !value;

  return (
    <>
      {label &&
        <TextContainer>
          <Typography variant="subtitle1" fontWeight="400">
            {label}
          </Typography>
        </TextContainer>
      }
      <StyledInputBoxContainer>
        <FormControlBox variant="outlined">
          <Select
            required={isRequired ? true : false}
            name={fieldName}
            variant="outlined"
            autoFocus={autoFocus}
            autoComplete={fieldName}
            IconComponent={ExpandMoreIcon}
            value={value}
            onChange={onChange}
            disabled={isDisabled}
            fullWidth={true}
            error={!!error}
            displayEmpty
            id={id}
            data-testid={dataTestId}
            MenuProps={{
              anchorOrigin: { vertical: 'bottom', horizontal: 'left' },
              transformOrigin: { vertical: 'top', horizontal: 'left' },
              PaperProps: {
                sx: {
                  maxHeight: 32 * 6 + 8,
                },
              },
            }}
            sx={{
              height: 45,
              position: 'relative',
              '& .MuiSelect-icon': {
                color: myVJBlack900,
                fontSize: 26,
                right: 9,
              },
              '& .MuiSelect-select': {
                color: showPlaceholder ? myVJBlack400 : myVJBlack900,
              },
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: myVJBlack200, // default border color
              },
              '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                borderColor: myVJBlue200, // color when focused
                borderWidth: '2px',
              },
              '&:hover .MuiOutlinedInput-notchedOutline': {
                borderColor: myVJBlack200, // color on hover
              },
            }}
          >
            {placeholder &&
              <MenuItem value="" disabled hidden>
                {placeholder}
              </MenuItem>
            }
            {options.map(({ value, text }) => (
              <MenuItem
                key={value}
                value={value}
                sx={{
                  color: myVJBlack400,
                  '&:hover': {
                    color: myVJBlack900,
                    backgroundColor: myVJBlack100,
                  },
                }}>
                {text}
              </MenuItem>
            ))}
          </Select>
          {error && (
            <ErrorContainer>
              <ExclamationError errorText={error} />
            </ErrorContainer>
          )}
        </FormControlBox>
      </StyledInputBoxContainer>
    </>
  );
}

SelectInput.displayName = "SelectInput";

export { SelectInput };